import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./App.css";
import Header from "./Header";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "./Loader";
import PropTypes from "prop-types";

const Form = ({ admissionDetails }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    studentName: "",
    fathersName: "",
    mobile: "",
    email: "",
    course: "",
    tutionFees: "",
    dob: "",
    admissionNumber: "",
    year: "",
  });

  const [errors, setErrors] = useState({});
  // Track the current toast ID
  const [activeToastId, setActiveToastId] = useState(null);

function formatToYYYYMMDD(isoDate) {
    if (!isoDate) return ""; 
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    if (!admissionDetails || !admissionDetails.studentData) {
      navigate("/ladyirwin");
      return;
    }

    const newDate = formatToYYYYMMDD(admissionDetails.studentData.dob) || "";

    setFormData((prev) => ({
      ...prev,
      studentName: admissionDetails.studentData.studentName || "",
      fathersName: admissionDetails.studentData.fathersName || "",
      course: admissionDetails.studentData.course || "",
      tutionFees: admissionDetails.studentData.tutionFees || "",
      admissionNumber: admissionDetails.studentData.admissionNumber || "",
      year: admissionDetails.studentData.year || "",
      dob: newDate || "",
      mobile: admissionDetails.studentData.mobile || "",
    }));
  }, [admissionDetails, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.studentName.trim()) {
      newErrors.studentName = "Student name is required";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.studentName)) {
      newErrors.studentName =
        "Name should not contain numbers or special characters";
    }

    if (!formData.fathersName.trim()) {
      newErrors.fathersName = "Parent's name is required";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.fathersName)) {
      newErrors.fathersName =
        "Name should not contain numbers or special characters";
    }

    if (!formData.dob.trim()) {
      newErrors.dob = "Date of Birth is required";
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // Only show a new toast if no toast is active
      if (!activeToastId || !toast.isActive(activeToastId)) {
        toast.dismiss(); // Clear any existing toasts for safety
        const toastId = toast.error(
          "Please fill all the required details correctly",
          {
            onClose: () => setActiveToastId(null), // Clear toast ID when toast closes
          }
        );
        setActiveToastId(toastId); // Store the new toast ID
      }
      return;
    }

    setIsLoading(true);

    try {
      const transactionId = generateTransactionId();
      const receiptNo = generateReceiptNumber();

      const data = {
        ...formData,
        transaction_id: transactionId,
        receipt_no: receiptNo,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/ladyirwin-api/get-atom-token-id`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`Form submission error! status: ${response.status}`);
      }

      const result = await response.json();
      sessionStorage.setItem("feeToken", formData.studentName);

      window.openPay(
        result.tokenData.atomTokenId,
        result.merchant_id,
        formData.email,
        formData.mobile
      );
    } catch (error) {
      console.error("Error:", error);
      // Only show error toast if no toast is active
      if (!activeToastId || !toast.isActive(activeToastId)) {
        toast.dismiss(); // Clear any existing toasts
        const toastId = toast.error("An error occurred during submission", {
          onClose: () => setActiveToastId(null), // Clear toast ID when toast closes
        });
        setActiveToastId(toastId); // Store the new toast ID
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateTransactionId = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const date = now.getDate().toString().padStart(2, "0");
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const seconds = now.getSeconds().toString().padStart(2, "0");
    const milliseconds = now.getMilliseconds().toString().padStart(3, "0");

    return `NDPS${year}${month}${date}${hours}${minutes}${seconds}${milliseconds}`;
  };

  function generateReceiptNumber() {
    const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let receiptNumber = "";

    const randomBytes = new Uint8Array(10);
    window.crypto.getRandomValues(randomBytes);

    receiptNumber += alphabets.charAt(randomBytes[0] % alphabets.length);
    receiptNumber += alphabets.charAt(randomBytes[1] % alphabets.length);

    for (let i = 2; i < randomBytes.length; i++) {
      receiptNumber += (randomBytes[i] % 10).toString();
    }
    return receiptNumber;
  }

  if (!admissionDetails || !admissionDetails.studentData) {
    return null;
  }
  return (
    <div>
      <Header />
      <main>
        <div className="form-container">
          <form onSubmit={handleSubmit}>
            <section className="form-section">
              <h2>Student Details</h2>
              <div className="details-form">
                <div className="form-group">
                  <label htmlFor="studentName">
                    Student's Name <span className="span">*</span>
                  </label>
                  <input
                    type="text"
                    id="studentName"
                    name="studentName"
                    value={formData.studentName}
                    onChange={handleChange}
                    placeholder="Enter your Name"
                    className="large-input-student-details"
                  />
                  {errors.studentName && (
                    <span className="error-message">{errors.studentName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="fathersName">
                    Parent's Name <span className="span">*</span>
                  </label>
                  <input
                    type="text"
                    id="fathersName"
                    name="fathersName"
                    value={formData.fathersName}
                    onChange={handleChange}
                    placeholder="Enter Parent's Name"
                    className="large-input-student-details"
                  />
                  {errors.fathersName && (
                    <span className="error-message">{errors.fathersName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="dob">
                    Date of Birth <span className="span">*</span>
                  </label>
                  <input
                    type="date"
                    id="dob"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    className="large-input-student-details"
                  />
                  {errors.dob && (
                    <span className="error-message">{errors.dob}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="mobile">
                    Student's Mobile Number <span className="span">*</span>
                  </label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter valid Mobile No"
                    className="large-input-student-details"
                  />
                  {errors.mobile && (
                    <span className="error-message">{errors.mobile}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Email ID <span className="span">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter valid Email Address"
                    className="large-input-student-details"
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="year">
                    Year <span className="span">*</span>
                  </label>
                  <input
                    type="text"
                    id="year"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    className="large-input-student-details"
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="course">
                    Course <span className="span">*</span>
                  </label>
                  <input
                    type="text"
                    id="course"
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    className="large-input-student-details"
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tutionFees">
                    Tuition Fees <span className="span">*</span>
                  </label>
                  <input
                    type="text"
                    id="tutionFees"
                    name="tutionFees"
                    value={formData.tutionFees}
                    onChange={handleChange}
                    className="large-input-student-details"
                    readOnly
                  />
                </div>
              </div>
            </section>
            <button type="submit" className="submit-btn" disabled={isLoading}>
              {isLoading ? "Processing..." : "Submit"}
            </button>
          </form>
          {isLoading && <Loader />}
        </div>
      </main>
      {/* Limit to one toast and configure autoClose */}
      <ToastContainer limit={1} autoClose={3000} />
    </div>
  );
};

Form.propTypes = {
  admissionDetails: PropTypes.shape({
    studentData: PropTypes.shape({
      studentName: PropTypes.string.isRequired,
      fathersName: PropTypes.string,
      mobile: PropTypes.string,
      email: PropTypes.string,
      course: PropTypes.string,
      tutionFees: PropTypes.number,
      dob: PropTypes.string,
      admissionNumber: PropTypes.string,
    }),
  }),
};

Form.propTypes = {
  admissionDetails: PropTypes.shape({
    admissionType: PropTypes.string.isRequired,
    admissionNumber: PropTypes.string.isRequired,
    studentData: PropTypes.shape({
      studentName: PropTypes.string,
      fathersName: PropTypes.string,
      mobile: PropTypes.string,
      email: PropTypes.string,
      course: PropTypes.string,
      tutionFees: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      dob: PropTypes.string,
      admissionNumber: PropTypes.string,
      year: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default Form;

